import { Mutex } from "async-mutex";
import { List, Map } from "immutable";

import { type Action, QueryEvaluator } from "../QueryEvaluator";
import {
	type ActionIntent,
	type ColumnAppendActionIntent,
	type ColumnDeleteActionIntent,
	type ColumnRenameActionIntent,
	type ColumnTypeChangeActionIntent,
} from "../QueryEvaluator/ActionIntent";
import { type QueryEvaluatorState } from "../QueryEvaluator/QueryManagerState";
import { type TableMutation } from "../QueryEvaluator/TableMutation";
import { type RunnableQuery } from "../RunnableQuery";
import { Tuple, idOf } from "../Tuple";
import { deleteChange, insert } from "../Tuples";
import { type ZSet } from "../ZSet";
import { jsonpathRef } from "../operatorsV2";
import { type TopLevelQuerySpec, compoundQueryKey } from "../operatorsV2/QuerySpec";
import { tupleComparatorFromOrderBySpec } from "../operatorsV2/orderedTopK";
import type { SqliteColumnDef, TypeInfo } from "../schema";
import { makeReactionTableName } from "../sqlite/makeReactionTableName";
import { type DatabaseTables, type TableName } from "../types";
import {
	type V1BaseSpec as BaseSpec,
	type V1TablePermission as TablePermission,
	type V1TableSpec as TableSpec,
	type V1ViewColumnDef as ViewColumnDef,
} from "../v1-base-spec";
import { type BaseSpecPersistence } from "./BaseSpecPersistence";
import { Query } from "./Query";
import { type QueryPersistence } from "./QueryPersistence";
import {
	assertTableExists,
	assertViewExists,
	mutateAppendColumnToAllViews,
	mutateAppendColumnToTable,
	mutateCreateReactionTable,
	mutateDeleteColumnFromAllViews,
	mutateDeleteColumnFromTable,
	mutateRemoveReactionTable,
	mutateRenameColumnFromAllViews,
	mutateRenameColumnFromTable,
	mutateUpdateColumnFromTable,
	newUniqueColumn,
} from "./base-utils";

type Subscriber<T> = {
	onChange: (results: T[], changes: TableMutation<T>[]) => void;
	subscriptionKey: string;
};

type MetadataSubscriber = {
	onChange: (baseSpec: BaseSpec) => void;
	subscriptionKey: string;
};

export type Subscription = () => void;

export interface QueryClient<Tables extends DatabaseTables> {
	from<T extends TableName<Tables>>(table: T): Query<Tables, T>;
	latestEpoch: number;
	pendingActions: List<ActionIntent>;
	reconcileActions(sourceOfTruth: List<Action>): Promise<void>;
	subscribeQuery<T>(
		query: TopLevelQuerySpec,
		onChange: (results: T[], changes: TableMutation<T>[]) => void
	): Subscription;
	subscribeMetadata(onChange: (baseSpec: BaseSpec) => void): Subscription;
	insert(
		tableId: TableName<Tables>,
		id: string,
		thing: Tables[TableName<Tables>]
	): Promise<QueryEvaluatorState>;
	delete(tableId: TableName<Tables>, id: string): Promise<QueryEvaluatorState>;
	isStoreOwner: boolean;
	userHasStoragePermission(
		expectedIs: TablePermission["is"]
	): (table: TableName<Tables>, userId: string | undefined) => boolean;
}

export class Store<Tables extends DatabaseTables> implements QueryClient<Tables> {
	private _evaluator: QueryEvaluator;
	private _queryPersistence: QueryPersistence;
	private _baseSpecPersistence: BaseSpecPersistence;
	private _subscriptions: Map<string, List<Subscriber<unknown>>>;
	private _metadataSubscriptions: List<MetadataSubscriber>;
	private _isStoreOwner: boolean;
	private _lock = new Mutex();

	constructor(
		queryPersistence: QueryPersistence,
		baseSpecPersistence: BaseSpecPersistence,
		isStoreOwner: boolean
	) {
		this._evaluator = new QueryEvaluator(
			queryPersistence.localStateReplica().tables.toObject()
		);
		this._queryPersistence = queryPersistence;
		this._baseSpecPersistence = baseSpecPersistence;
		this._evaluator.ensureReconciled(this._queryPersistence.localStateReplica());
		this._subscriptions = Map();
		this._metadataSubscriptions = List();
		this._isStoreOwner = isStoreOwner;
	}

	get status(): "active" | Error {
		if (this._evaluator.latestState.error) {
			return new Error(this._evaluator.latestState.error);
		}
		return "active";
	}

	get latestEpoch(): number {
		return this._evaluator.latestEpoch;
	}

	get latestState() {
		return this._evaluator.latestState;
	}

	async updateViewTitle(viewId: string, title: string): Promise<void> {
		const newBaseSpec = structuredClone(this._baseSpecPersistence.localBaseSpecReplica());
		if (!newBaseSpec.views[viewId]) {
			throw new Error(`View ${viewId} not found`);
		}
		newBaseSpec.views[viewId].viewTitle = title;
		await this._baseSpecPersistence.saveBaseSpec(newBaseSpec);
		this._notifyMetadataSubscribers(newBaseSpec);
	}

	async createViewIfNotExists(
		viewId: string,
		table: TableName<Tables>,
		tableSpec: TableSpec
	): Promise<void> {
		const newBaseSpec = structuredClone(this._baseSpecPersistence.localBaseSpecReplica());

		//
		// Create table if it doesn't exist.
		//

		if (newBaseSpec.views[viewId] || newBaseSpec.tables[table]) {
			return;
		}
		newBaseSpec.tables[table] = tableSpec;

		//
		// Create view if it doesn't exist.
		//

		const query = this.from(table).spec;
		newBaseSpec.views[viewId] = {
			query,
			columns: tableSpec.columns.map((column) => ({
				linkedTable: table,
				linkedColumn: column.name,
			})),
		};

		//
		// Save changes.
		//

		await this._baseSpecPersistence.saveBaseSpec(newBaseSpec);
		this._notifyMetadataSubscribers(newBaseSpec);
	}

	async createViewFromTable(viewId: string, table: TableName<Tables>): Promise<void> {
		const newBaseSpec = structuredClone(this._baseSpecPersistence.localBaseSpecReplica());

		//
		// Create view from table, if it exists.
		//

		const tableSpec = assertTableExists(newBaseSpec, table);
		const query = this.from(table).spec;
		newBaseSpec.views[viewId] = {
			query,
			columns: tableSpec.columns.map((column) => ({
				linkedTable: table,
				linkedColumn: column.name,
			})),
		};

		//
		// Save changes.
		//

		await this._baseSpecPersistence.saveBaseSpec(newBaseSpec);
		this._notifyMetadataSubscribers(newBaseSpec);
	}

	async appendColumn(table: TableName<Tables>): Promise<ColumnAppendActionIntent> {
		//
		// Create new column definitions.
		//

		const newBaseSpec = structuredClone(this._baseSpecPersistence.localBaseSpecReplica());
		const columns = assertTableExists(newBaseSpec, table).columns;

		//
		// Update the base columns, and any view that depends on it.
		//

		const { sqliteColumnDef, viewColumnDef } = newUniqueColumn(table, columns);
		mutateAppendColumnToTable(newBaseSpec, table, sqliteColumnDef);
		mutateAppendColumnToAllViews(newBaseSpec, table, viewColumnDef);

		//
		// Persist the metadata changes.
		//

		await this._baseSpecPersistence.saveBaseSpec(newBaseSpec);
		this._notifyMetadataSubscribers(newBaseSpec);

		//
		// Declare intent to append the column.
		//

		const intent = this._evaluator.columnAppendIntent(table, sqliteColumnDef, viewColumnDef);

		//
		// Attempt to persist changes to state and reconcile local changes.
		//

		await this._queryPersistence.ensurePersistedState(intent);
		this._evaluator.ensureReconciled(this._queryPersistence.localStateReplica().actionLog);
		this._notifyQuerySubscribersById(intent.changesByQueryId, intent.proposedState);

		//
		// Return.
		//

		return intent;
	}

	async deleteColumn(table: string, columnName: string): Promise<ColumnDeleteActionIntent> {
		//
		// Update the base columns, and any view that depends on it.
		//

		const newBaseSpec = structuredClone(this._baseSpecPersistence.localBaseSpecReplica());

		mutateDeleteColumnFromTable(newBaseSpec, table, columnName);
		mutateDeleteColumnFromAllViews(newBaseSpec, table, columnName);

		//
		// Persist the metadata changes.
		//

		await this._baseSpecPersistence.saveBaseSpec(newBaseSpec);
		this._notifyMetadataSubscribers(newBaseSpec);

		//
		// Declare intent to delete the column.
		//

		const deleteIntent = this._evaluator.columnDeleteIntent(table, columnName);

		//
		// Attempt to persist changes to state and reconcile local changes.
		//

		await this._queryPersistence.ensurePersistedState(deleteIntent);
		this._evaluator.ensureReconciled(this._queryPersistence.localStateReplica().actionLog);
		this._notifyQuerySubscribersById(deleteIntent.changesByQueryId, deleteIntent.proposedState);

		//
		// Return.
		//

		return deleteIntent;
	}

	async renameColumn(
		table: TableName<Tables>,
		oldColumnName: string,
		newHeader: string,
		newColumnName: string
	): Promise<ColumnRenameActionIntent> {
		//
		// Update the base columns, and any view that depends on it.
		//

		const newBaseSpec = structuredClone(this._baseSpecPersistence.localBaseSpecReplica());

		mutateRenameColumnFromTable(newBaseSpec, table, oldColumnName, newHeader, newColumnName);
		mutateRenameColumnFromAllViews(newBaseSpec, table, oldColumnName, newColumnName);

		//
		// Handle Reaction column special case.
		//

		const tableSpec = assertTableExists(newBaseSpec, table);
		const columnIndex = tableSpec.columns.findIndex((c) => c.name === newColumnName);
		const column = tableSpec.columns[columnIndex];

		if (column?.typeInfo?.enrichedType === "Reaction") {
			// For Reaction columns, we need to update the linkedTable and query
			const oldReactionTableName = makeReactionTableName(table, oldColumnName);
			const newReactionTableName = makeReactionTableName(table, newColumnName);

			// Rename the reaction table in the base spec
			if (newBaseSpec.tables[oldReactionTableName]) {
				newBaseSpec.tables[newReactionTableName] = newBaseSpec.tables[oldReactionTableName];
				delete newBaseSpec.tables[oldReactionTableName];
			}

			// Update linkedTable and query in all views
			for (const [viewId, viewSpec] of Object.entries(newBaseSpec.views)) {
				if (table !== viewSpec.query.spec[0].table) {
					continue;
				}

				const viewColumnIndex = viewSpec.columns.findIndex(
					(c) => c.linkedColumn === newColumnName
				);
				if (viewColumnIndex !== -1) {
					const viewColumn = viewSpec.columns[viewColumnIndex];
					viewSpec.columns[viewColumnIndex] = {
						...viewColumn,
						linkedTable: newReactionTableName,
					};

					// Update the view's query to use the new column name
					const newQuery = this.from(table).select({
						[`$.${newColumnName}`]: this.from(
							newReactionTableName as TableName<Tables>
						).where(({ pluck }) => [
							pluck(newReactionTableName as TableName<Tables>, "$.linkedRecordId"),
							"=",
							pluck(table, "$.id"),
						]),
					});
					viewSpec.query = newQuery.spec;
				}
			}
		}

		//
		// Persist the metadata changes.
		//

		await this._baseSpecPersistence.saveBaseSpec(newBaseSpec);
		this._notifyMetadataSubscribers(newBaseSpec);

		//
		// Register any updated queries with the evaluator.
		//

		for (const [viewId, viewSpec] of Object.entries(newBaseSpec.views)) {
			if (table !== viewSpec.query.spec[0].table) {
				continue;
			}

			const viewColumnIndex = viewSpec.columns.findIndex(
				(c) => c.linkedColumn === newColumnName
			);
			if (viewColumnIndex !== -1) {
				// Register the updated query with the evaluator
				await this.registerQuery(viewSpec.query);
			}
		}

		//
		// Declare intent to rename the column.
		//

		const intent = this._evaluator.columnRenameIntent(
			table,
			oldColumnName,
			newHeader,
			newColumnName
		);

		//
		// Attempt to persist changes to state and reconcile local changes.
		//

		await this._queryPersistence.ensurePersistedState(intent);
		this._evaluator.ensureReconciled(this._queryPersistence.localStateReplica().actionLog);
		this._notifyQuerySubscribersById(intent.changesByQueryId, intent.proposedState);

		//
		// Return.
		//

		return intent;
	}

	async updateColumn(
		table: TableName<Tables> | undefined,
		columnName: string,
		partialColumn: Partial<Omit<SqliteColumnDef, "name" | "header">>
	): Promise<void> {
		if (!table) {
			throw new Error("Table not found");
		}

		//
		// Update the base columns, and any view that depends on it.
		//

		const newBaseSpec = structuredClone(this._baseSpecPersistence.localBaseSpecReplica());
		const tableSpec = assertTableExists(newBaseSpec, table);
		const index = (tableSpec.columns || []).findIndex((c) => c.name === columnName);
		const oldColumn = tableSpec.columns[index];
		if (index === -1 || !oldColumn) {
			throw new Error(`Column with accessor key ${columnName} not found`);
		}
		const column = { ...oldColumn, ...partialColumn };

		mutateUpdateColumnFromTable(newBaseSpec, table, column);

		//
		// Persist the metadata changes.
		//

		await this._baseSpecPersistence.saveBaseSpec(newBaseSpec);
		this._notifyMetadataSubscribers(newBaseSpec);
	}

	async updateColumnType(
		table: TableName<Tables> | undefined,
		columnName: string,
		oldTypeInfo: TypeInfo,
		newTypeInfo: TypeInfo
	): Promise<ColumnTypeChangeActionIntent> {
		return await this._lock.runExclusive(async () => {
			if (!table) {
				throw new Error("Table not found");
			}

			//
			// Check if this is a change to Reaction type.
			//

			const isChangingToReaction =
				newTypeInfo?.enrichedType === "Reaction" &&
				oldTypeInfo?.enrichedType !== "Reaction";

			const isChangingFromReaction =
				oldTypeInfo?.enrichedType === "Reaction" &&
				newTypeInfo?.enrichedType !== "Reaction";

			//
			// Create the reaction table name and table spec.
			//

			const reactionTableName = makeReactionTableName(table, columnName);

			//
			// Update the base spec to include the new reaction table and update the column type.
			//

			const newBaseSpec = structuredClone(this._baseSpecPersistence.localBaseSpecReplica());

			if (isChangingToReaction) {
				// Add the reaction table to the base spec
				mutateCreateReactionTable(newBaseSpec, reactionTableName);
			} else if (isChangingFromReaction) {
				// Remove the reaction table from the base spec
				mutateRemoveReactionTable(newBaseSpec, reactionTableName);
			}

			// Update the column type in the original table
			const tableSpec = assertTableExists(newBaseSpec, table);
			const index = (tableSpec.columns || []).findIndex((c) => c.name === columnName);
			const oldColumn = tableSpec.columns[index];
			if (index === -1 || !oldColumn) {
				throw new Error(`Column with accessor key ${columnName} not found`);
			}
			const column = { ...oldColumn, typeInfo: newTypeInfo };
			mutateUpdateColumnFromTable(newBaseSpec, table, column);

			// Update linkedTable in all views that reference this column
			for (const [viewId, viewSpec] of Object.entries(newBaseSpec.views)) {
				if (table !== viewSpec.query.spec[0].table) {
					continue;
				}

				const viewColumnIndex = viewSpec.columns.findIndex(
					(c) => c.linkedColumn === columnName
				);
				if (viewColumnIndex !== -1) {
					const viewColumn = viewSpec.columns[viewColumnIndex];
					viewSpec.columns[viewColumnIndex] = {
						...viewColumn,
						linkedTable: isChangingToReaction ? reactionTableName : table,
					};

					// Update the view's query to include the reaction subquery
					if (isChangingToReaction) {
						// Create a new query with leftOuterJoin for the reaction column
						const newQuery = this.from(table).select({
							[`$.${columnName}`]: this.from(
								reactionTableName as TableName<Tables>
							).where(({ pluck }) => [
								pluck(reactionTableName as TableName<Tables>, "$.linkedRecordId"),
								"=",
								pluck(table, "$.id"),
							]),
						});
						viewSpec.query = newQuery.spec;
					} else if (isChangingFromReaction) {
						// Revert to a simple query without the reaction subquery
						viewSpec.query = this.from(table).spec;
					}
				}
			}

			//
			// Persist the metadata changes.
			//

			await this._baseSpecPersistence.saveBaseSpec(newBaseSpec);
			this._notifyMetadataSubscribers(newBaseSpec);

			//
			// Declare intent to change the column type.
			//

			const intent = this._evaluator.columnTypeChangeIntent(
				table,
				columnName,
				oldTypeInfo,
				newTypeInfo
			);

			//
			// Attempt to persist changes to state and reconcile local changes.
			//

			await this._queryPersistence.ensurePersistedState(intent);
			this._evaluator.ensureReconciled(this._queryPersistence.localStateReplica());
			this._notifyQuerySubscribersById(intent.changesByQueryId, intent.proposedState);

			//
			// Return.
			//

			return intent;
		});
	}

	async updateColumnDisplayOptions(
		viewId: string,
		columnName: string,
		displayOptions: Partial<Omit<ViewColumnDef, "linkedColumn">>
	): Promise<void> {
		//
		// Update the base columns, and any view that depends on it.
		//

		const newBaseSpec = structuredClone(this._baseSpecPersistence.localBaseSpecReplica());
		const viewSpec = assertViewExists(newBaseSpec, viewId);

		const index = (viewSpec.columns || []).findIndex((c) => c.linkedColumn === columnName);
		const oldDisplayOptions = viewSpec.columns[index];
		if (index === -1 || !oldDisplayOptions) {
			throw new Error(`Column with accessor key ${columnName} not found`);
		}
		const newDisplayOpts: ViewColumnDef = { ...oldDisplayOptions, ...displayOptions };
		viewSpec.columns[index] = newDisplayOpts;

		if (displayOptions.primaryDisplayColumn) {
			for (const column of viewSpec.columns) {
				if (column.primaryDisplayColumn && column.linkedColumn !== columnName) {
					column.primaryDisplayColumn = false;
				}
			}
		}

		//
		// Persist the metadata changes.
		//

		await this._baseSpecPersistence.saveBaseSpec(newBaseSpec);
		this._notifyMetadataSubscribers(newBaseSpec);
	}

	// Start QueryClient implementation ///////////////////////////////////////////////////////////

	from<T extends TableName<Tables>>(table: T): Query<Tables, T> {
		return new Query<Tables, T>(this, table);
	}

	get pendingActions(): List<ActionIntent> {
		return this._evaluator.pendingActions;
	}

	async reconcileActions(sourceOfTruth: List<Action>) {
		const { changesByQueryId } = this._evaluator.ensureReconciled(sourceOfTruth);
		this._notifyQuerySubscribersById(changesByQueryId, this._evaluator.latestState);
	}

	async insert(tableId: TableName<Tables>, id: string, thing: Tables[TableName<Tables>]) {
		return this.applyMutation(tableId, insert(Tuple.from<unknown>(id, undefined, thing)));
	}

	async delete(tableId: TableName<Tables>, id: string) {
		return this.applyMutation(tableId, deleteChange(id));
	}

	get isStoreOwner(): boolean {
		return this._isStoreOwner;
	}

	userHasStoragePermission(expectedIs: TablePermission["is"]) {
		return (table: TableName<Tables>, userId: string | undefined): boolean => {
			if (this._isStoreOwner) {
				return true;
			}
			const tableSpec = assertTableExists(
				this._baseSpecPersistence.localBaseSpecReplica(),
				table
			);
			const can = tableSpec.permissions.some((p) => {
				return (
					p.is === expectedIs &&
					(p.subject === "group:all" ||
						(userId &&
							(p.subject === "group:loggedIn" || p.subject === `user:${userId}`)))
				);
			});
			return can;
		};
	}

	subscribeQuery<T>(
		_query: TopLevelQuerySpec,
		onChange: (results: T[], changes: TableMutation<T>[]) => void
	): Subscription {
		this._evaluator.ensureReconciled(this._queryPersistence.localStateReplica());
		//
		// IMPORTANT: `structuredClone` PREVENTS CRASHES. This object often comes out of SQLite and
		// is sometimes frozen. `structuredClone` allows us to mutate it without crashing the
		// component.
		//
		// IF YOU REMOVE THIS:
		//
		//   - Check that clicking `Start Blank` button in the `<BaseView />` works.
		//   - Look at `<BaseView />` instances with no sort order in their query.
		//
		// If neither of these crashes the app, you *MIGHT* be ok to continue.
		//
		const query = structuredClone(_query);

		const subscriptionKey = crypto.randomUUID();
		const queryKey = compoundQueryKey({
			querySpec: query.spec,
			permissionSpec: query.permissionFilter ?? { type: "read_all_rows" },
		});
		const subscriber: Subscriber<T> = { onChange, subscriptionKey };

		if (!query.finalizer) {
			query.finalizer = {
				type: "orderedTopK",
				orderBy: jsonpathRef(query.spec[0].table, `$.id`),
			};
		}

		// Add subscriber to the map
		this._subscriptions = this._subscriptions.update(queryKey, (subscribers = List()) =>
			subscribers.push(subscriber as Subscriber<unknown>)
		);

		// Register the query and get initial results
		void this.registerQuery(query).then((results) => {
			// FIXME. All of this is insane lol.
			const { proposedState } = results;
			const lastResults = proposedState.queryResults.get(queryKey)?.toArray();
			if (!lastResults || lastResults.length === 0) {
				return;
			}

			const resultsAsChange = List(lastResults.map(({ tuple }) => insert(tuple)));

			// Only deliver to the subscriber that requested the initial results.
			const currSubscriber = this._subscriptions
				.get(queryKey)
				?.find((s) => s.subscriptionKey === subscriptionKey);
			const queryResults = proposedState.queryResults.get(queryKey);
			const querySpec = proposedState.querySpecs.get(queryKey);
			if (!currSubscriber || !queryResults || !querySpec) {
				throw new Error(`Query ${queryKey} not found`);
			}

			this._notifyQuerySubscribers(
				List([currSubscriber]),
				queryResults,
				querySpec,
				resultsAsChange
			);
		});

		// Return unsubscribe function
		return () => {
			// Remove this subscriber from the map
			this._subscriptions = this._subscriptions.update(queryKey, (subscribers) => {
				if (!subscribers) {
					return List();
				}
				return subscribers.filter((s) => s.subscriptionKey !== subscriptionKey);
			});

			// If no more subscribers for this query, unregister it
			if (this._subscriptions.get(queryKey)?.isEmpty()) {
				// FIXME: Implement query unregistration
			}
		};
	}

	subscribeMetadata(onChange: (baseSpec: BaseSpec) => void): Subscription {
		const subscriptionKey = crypto.randomUUID();
		const subscriber: MetadataSubscriber = { onChange, subscriptionKey };

		this._metadataSubscriptions = this._metadataSubscriptions.push(subscriber);
		onChange(this._baseSpecPersistence.localBaseSpecReplica());

		// Return unsubscribe function
		return () => {
			this._metadataSubscriptions = this._metadataSubscriptions.filter(
				(sub) => sub.subscriptionKey !== subscriptionKey
			);
		};
	}

	// End QueryClient implementation /////////////////////////////////////////////////////////////

	private async registerQuery(query: TopLevelQuerySpec) {
		return await this._lock.runExclusive(async () => {
			const proposedState = this._evaluator.queryIntent(query);
			await this._queryPersistence.ensurePersistedState(proposedState);
			this._evaluator.ensureReconciled(this._queryPersistence.localStateReplica());
			return proposedState;
		});
	}

	async applyMutation(
		tableId: string,
		mutation: TableMutation<unknown>
	): Promise<QueryEvaluatorState> {
		return await this._lock.runExclusive(async () => {
			this._evaluator.ensureReconciled(this._queryPersistence.localStateReplica());
			const lastSeenEpoch = this._evaluator.latestEpoch;
			const mutationIntent = this._evaluator.mutationIntent(tableId, lastSeenEpoch, mutation);
			const { proposedState, changesByQueryId } = mutationIntent;
			this._notifyQuerySubscribersById(changesByQueryId, proposedState);

			await this._queryPersistence.ensurePersistedState(mutationIntent);
			this._evaluator.ensureReconciled(
				this._queryPersistence.localStateReplica(),
				mutationIntent
			);

			return proposedState;
		});
	}

	private _notifyMetadataSubscribers(baseSpec: BaseSpec) {
		this._metadataSubscriptions.forEach((subscriber) => {
			subscriber.onChange(baseSpec);
		});
	}

	private _notifyQuerySubscribersById(
		changesByQueryId: Map<string, List<TableMutation<unknown>>>,
		state: QueryEvaluatorState
	) {
		for (const [queryId, changes] of changesByQueryId) {
			const subscribers = this._subscriptions.get(queryId);
			if (!subscribers) {
				continue;
			}
			const querySpec = state.querySpecs.get(queryId);
			const queryResults = state.queryResults.get(queryId);
			if (!querySpec || !queryResults) {
				continue;
			}

			this._notifyQuerySubscribers(subscribers, queryResults, querySpec, changes);
		}
	}

	private _notifyQuerySubscribers(
		subscribers: List<Subscriber<unknown>>,
		queryResults: ZSet<unknown>,
		querySpec: RunnableQuery,
		changes: List<TableMutation<unknown>>
	) {
		//
		// FIXME: we should do this server-side. ℤ-sets do not represent ordering, so if there
		// is an ordered top-k "finalizer", we need to sort the results before sending them to
		// the subscriber.
		//

		let finalizedResults = queryResults;
		if (querySpec.finalizerSpec) {
			finalizedResults = queryResults.sort(
				tupleComparatorFromOrderBySpec(querySpec.finalizerSpec),
				querySpec.finalizerSpec?.direction ?? "ASC"
			);
		} else {
			// FIXME: Horribly inefficient.
			finalizedResults = queryResults.sort(
				(a) => Tuple.from(a.id, a.getOwner(), parseInt(idOf(a)!)),
				"ASC"
			);
		}

		subscribers.forEach((subscriber) => {
			subscriber.onChange(finalizedResults.toResultArray(), changes.toArray());
		});
	}
}

// // Usage:
// const db = new Store({ directors: {}, movies: {} });
// db.from("directors")
// 	.where("$.name", "=", "Peter Jackson")
// 	.select({
// 		"$.films": ({ jsonGroupArray, valAtPath }) =>
// 			db
// 				.from("movies")
// 				.where("$.director.name", "=", valAtPath("directors", "$.name"))
// 				.orderBy("$.year")
// 				.select(jsonGroupArray("movies", "$")),
// 	});

// const query = db
// 	.from("directors")
// 	.where(({ eq, pluck }) => eq(pluck("$.name"), "Peter Jackson"))
// 	.select({
// 		"$.films": ({ jsonGroupArray, eq, pluck }) =>
// 			db
// 				.from("movies")
// 				.where(eq(pluck("$.director.name"), pluck("directors", "$.name")))
// 				.orderBy(pluck("$.year"))
// 				.select(jsonGroupArray("movies", "$")),
// 	});
